import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { getBlogPostBySlug, blogPosts } from '@/data/blogPosts';
import { AIAssistantWidget } from '@/components/AIAssistantWidget';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  const post = getBlogPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get related posts (other posts, excluding current one)
  const relatedPosts = blogPosts
    .filter(p => p.slug !== slug)
    .slice(0, 2);

  // Convert markdown-like content to HTML (basic implementation)
  const formatContent = (content: string) => {
    return content
      .split('\n')
      .map((paragraph, index) => {
        const trimmed = paragraph.trim();
        
        if (trimmed.startsWith('# ')) {
          return <h1 key={index} className="text-3xl md:text-4xl font-bold text-foreground mb-6 mt-8">{trimmed.replace('# ', '')}</h1>;
        }
        
        if (trimmed.startsWith('## ')) {
          return <h2 key={index} className="text-2xl md:text-3xl font-semibold text-foreground mb-4 mt-8">{trimmed.replace('## ', '')}</h2>;
        }
        
        if (trimmed.startsWith('### ')) {
          return <h3 key={index} className="text-xl font-semibold text-foreground mb-3 mt-6">{trimmed.replace('### ', '')}</h3>;
        }

        if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
          return <div key={index} className="font-bold text-foreground mb-4">{trimmed.replace(/\*\*/g, '')}</div>;
        }

        if (trimmed.startsWith('- ')) {
          return <li key={index} className="text-muted-foreground mb-2 ml-4">{trimmed.replace('- ', '')}</li>;
        }

        if (trimmed === '') {
          return <br key={index} />;
        }
        
        // Handle inline bold formatting within paragraphs
        if (trimmed.includes('**')) {
          const parts = trimmed.split(/(\*\*.*?\*\*)/);
          return (
            <p key={index} className="text-muted-foreground mb-4 leading-relaxed">
              {parts.map((part, partIndex) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={partIndex} className="font-bold text-foreground">{part.replace(/\*\*/g, '')}</strong>;
                }
                return part;
              })}
            </p>
          );
        }
        
        return <p key={index} className="text-muted-foreground mb-4 leading-relaxed">{trimmed}</p>;
      });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Dynamic SEO Meta Tags */}
      <title>{post.title} | Safurra Blog</title>
      <meta name="description" content={post.metaDescription} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.metaDescription} />
      <meta property="og:type" content="article" />
      <meta property="article:published_time" content={post.publishDate} />
      <meta property="article:author" content={post.author} />
      {post.tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.metaDescription,
          "datePublished": post.publishDate,
          "author": {
            "@type": "Person",
            "name": post.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Safurra",
            "logo": {
              "@type": "ImageObject",
              "url": "https://safurra.com/logo.png"
            }
          }
        })}
      </script>

      <Header />

      {/* Breadcrumb */}
      <nav className="py-4 bg-primary-light/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
              <span>/</span>
              <span className="text-foreground">{post.title}</span>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Article Header */}
      <article className="py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.publishDate)}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {post.author}
              </div>
            </div>

            {/* Article Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Featured Image */}
            {post.featuredImage && (
              <div className="w-full h-64 md:h-96 rounded-xl mb-8 overflow-hidden shadow-lg">
                <img 
                  src={post.featuredImage} 
                  alt={`Featured image for article: ${post.title}`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {/* Article Content */}
      <section className="pb-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              {formatContent(post.content)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Ready to Get Your Quote?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Use our simple calculator to determine how much coverage you need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/calculator">
                <Button size="lg" variant="hero">
                  Calculate Coverage
                </Button>
              </Link>
              <Link to="/">
                <Button size="lg" variant="outline">
                  Get Quote Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-foreground mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="group block p-6 border border-border rounded-xl hover:shadow-lg transition-all duration-300"
                  >
                    <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-3">
                      {relatedPost.title}
                    </h3>
                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {relatedPost.excerpt}
                    </p>
                    <div className="flex items-center text-primary text-sm font-medium">
                      Read more about {relatedPost.title.toLowerCase()}
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Navigation */}
      <section className="py-8 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <Link to="/blog">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Button>
            </Link>
            <Link to="/">
              <Button variant="hero">
                Get Your Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* AI Assistant Widget */}
      <AIAssistantWidget pageContext="blog" />
    </div>
  );
};

export default BlogPost;